import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, TrendingUp, Award, MessageSquare, Mail, BookOpen } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Legend
} from "recharts";

const OXBLOOD = "#722F37";
const INK = "#111111";
const MUTED = "#E8E4DB";

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="porcelain-card p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono-label text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-primary" />}
      </div>
      <div className="font-serif-display text-4xl leading-none mt-3">{value}</div>
      {sub && <div className="font-mono-label text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [drips, setDrips] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    Promise.all([
      api.get("/admin/overview").then((r) => r.data),
      api.get("/admin/drips").then((r) => r.data),
      api.get("/admin/tutor-questions").then((r) => r.data),
      api.get("/admin/recent-users").then((r) => r.data),
    ])
      .then(([o, d, q, u]) => { setOverview(o); setDrips(d); setQuestions(q); setRecentUsers(u); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [user]);

  if (authLoading) return <div className="p-10 font-mono-label text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="admin-denied">
        <div className="font-mono-label text-primary">403 · Admin only</div>
        <h1 className="font-serif-display text-4xl mt-4 font-light">This studio is staff-only.</h1>
        <p className="mt-4 text-muted-foreground">Only users marked as admin can view analytics.</p>
      </div>
    );
  }

  if (err) return <div className="p-10 text-destructive" data-testid="admin-error">{err}</div>;
  if (!overview) return <div className="p-10 font-mono-label text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="font-mono-label text-primary">Admin · Analytics</div>
      <h1 className="font-serif-display text-5xl md:text-6xl mt-3 font-light leading-none tracking-tighter">Studio control room</h1>
      <p className="mt-4 text-muted-foreground">Signup funnel, drip status, and tutor intelligence — all live.</p>

      {/* Top stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total users" value={overview.total_users} sub={`+${overview.users_week} THIS WEEK`} icon={Users} />
        <Stat label="Free → Pro" value={`${overview.conversion_percent}%`} sub={`${overview.premium_users} PREMIUM`} icon={TrendingUp} />
        <Stat label="Quiz attempts" value={overview.total_quiz_attempts} sub={`AVG ${overview.avg_quiz_score}%`} icon={Award} />
        <Stat label="Tutor sessions" value={overview.tutor_sessions} sub={`${questions?.total_user_messages ?? 0} MESSAGES`} icon={MessageSquare} />
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Users today" value={overview.users_today} sub="LAST 24H" icon={Users} />
        <Stat label="Lessons done" value={overview.total_lessons_completed} sub="ALL TIME" icon={BookOpen} />
        <Stat label="Premium" value={overview.premium_users} sub="PORCELAIN PRO" icon={Award} />
        <Stat label="Drips tracked" value={drips?.days?.reduce((s, d) => s + d.sent + d.scheduled + d.pending_api_key + d.failed, 0) ?? 0} sub="EMAILS QUEUED" icon={Mail} />
      </div>

      {/* Charts */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="porcelain-card p-6 lg:col-span-2" data-testid="admin-signups-chart">
          <div className="font-mono-label text-primary">Signups · Last 14 days</div>
          <div className="h-64 mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={overview.signups_series}>
                <CartesianGrid strokeDasharray="3 3" stroke={MUTED} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} style={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis allowDecimals={false} style={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${MUTED}`, borderRadius: 2, fontFamily: "Instrument Sans" }} />
                <Line type="monotone" dataKey="count" stroke={OXBLOOD} strokeWidth={2} dot={{ fill: OXBLOOD, r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="porcelain-card p-6" data-testid="admin-drips-chart">
          <div className="font-mono-label text-primary">Drip status · By day</div>
          <div className="h-64 mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={drips?.days || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={MUTED} />
                <XAxis dataKey="day" tickFormatter={(d) => `D${d}`} style={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis allowDecimals={false} style={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${MUTED}`, borderRadius: 2 }} />
                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }} />
                <Bar dataKey="sent" stackId="a" fill={OXBLOOD} />
                <Bar dataKey="scheduled" stackId="a" fill={INK} />
                <Bar dataKey="pending_api_key" stackId="a" fill="#C29B62" />
                <Bar dataKey="failed" stackId="a" fill="#999" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent users + tutor questions */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="porcelain-card p-6" data-testid="admin-recent-users">
          <div className="font-mono-label text-primary mb-4">Recent signups</div>
          <div className="border-t border-border">
            {recentUsers.length === 0 && <div className="py-6 font-mono-label text-muted-foreground">NO USERS YET</div>}
            {recentUsers.map((u) => (
              <div key={u.id} className="py-3 border-b border-border flex items-center justify-between">
                <div>
                  <div className="font-serif-display text-lg leading-none">{u.name}</div>
                  <div className="font-mono-label text-muted-foreground mt-1">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.is_admin && <span className="font-mono-label text-xs border border-foreground px-1.5 py-0.5">ADMIN</span>}
                  {u.is_premium && <span className="font-mono-label text-xs border border-primary text-primary px-1.5 py-0.5">PRO</span>}
                  <div className="font-mono-label text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="porcelain-card p-6" data-testid="admin-tutor-questions">
          <div className="font-mono-label text-primary mb-4">Latest tutor questions</div>
          <div className="border-t border-border max-h-96 overflow-y-auto">
            {(!questions?.recent || questions.recent.length === 0) && (
              <div className="py-6 font-mono-label text-muted-foreground">NO QUESTIONS YET</div>
            )}
            {questions?.recent?.map((q, i) => (
              <div key={i} className="py-3 border-b border-border">
                <div className="text-sm leading-relaxed">{q.content}</div>
                <div className="font-mono-label text-muted-foreground mt-2">{new Date(q.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
